// Dates as the product shows them, always in Europe/Rome whatever the
// browser's zone: the api answers in UTC with an offset.

export const TIME_ZONE = "Europe/Rome";

const dateTime = new Intl.DateTimeFormat("it-IT", {
  timeZone: TIME_ZONE,
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const time = new Intl.DateTimeFormat("it-IT", {
  timeZone: TIME_ZONE,
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const longDate = new Intl.DateTimeFormat("it-IT", {
  timeZone: TIME_ZONE,
  day: "numeric",
  month: "long",
  year: "numeric",
});

function parts(formatter: Intl.DateTimeFormat, value: Date): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of formatter.formatToParts(value)) out[p.type] = p.value;
  return out;
}

/** "06/10/2026 - 16:52" (the label of «News dall'ultimo accesso»). */
export function formatDateTime(iso: string): string {
  const p = parts(dateTime, new Date(iso));
  return `${p.day}/${p.month}/${p.year} - ${p.hour}:${p.minute}`;
}

/** "06/10/2026" from a date (YYYY-MM-DD) or a date-time. */
export function formatShortDate(iso: string): string {
  const value = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T12:00:00+02:00`) : new Date(iso);
  const p = parts(dateTime, value);
  return `${p.day}/${p.month}/${p.year}`;
}

/** "09:32". */
export function formatTime(iso: string): string {
  const p = parts(time, new Date(iso));
  return `${p.hour}:${p.minute}`;
}

/** "12 ottobre 2026" from a date (YYYY-MM-DD) or a date-time. */
export function formatLongDate(isoDate: string): string {
  // a bare date is a calendar day, not an instant: read it as noon in Rome so no zone shifts it
  const value = /^\d{4}-\d{2}-\d{2}$/.test(isoDate) ? new Date(`${isoDate}T12:00:00+02:00`) : new Date(isoDate);
  return longDate.format(value);
}

const weekdayDate = new Intl.DateTimeFormat("it-IT", {
  timeZone: TIME_ZONE,
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});

/** "giovedì 15 ottobre 2026" from a date (YYYY-MM-DD) or a date-time. */
export function formatWeekdayDate(isoDate: string): string {
  const value = /^\d{4}-\d{2}-\d{2}$/.test(isoDate) ? new Date(`${isoDate}T12:00:00+02:00`) : new Date(isoDate);
  return weekdayDate.format(value);
}

const isoDay = new Intl.DateTimeFormat("en-CA", {
  timeZone: TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** The day in Europe/Rome as YYYY-MM-DD (what «oggi» means for the sittings). */
export function romeDay(now: Date = new Date()): string {
  return isoDay.format(now);
}

/** "11:00" from the api's time of day ("11:00:00"); as it is when it has another shape. */
export function formatClock(time: string): string {
  const m = /^(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/.exec(time);
  return m ? `${m[1]}:${m[2]}` : time;
}

const MONTHS_IT = [
  "Gennaio",
  "Febbraio",
  "Marzo",
  "Aprile",
  "Maggio",
  "Giugno",
  "Luglio",
  "Agosto",
  "Settembre",
  "Ottobre",
  "Novembre",
  "Dicembre",
] as const;

/** "2026-10" -> "Ottobre"; an unknown month comes back as it is. */
export function monthName(yearMonth: string): string {
  const m = Number(yearMonth.slice(5, 7));
  return MONTHS_IT[m - 1] ?? yearMonth;
}

/** "Ottobre" -> "10"; unknown names come back as they are. */
export function monthNumber(name: string): string {
  const i = MONTHS_IT.findIndex((m) => m.toLowerCase() === name.toLowerCase());
  return i === -1 ? name : String(i + 1).padStart(2, "0");
}

/**
 * The label of a week of a month in the date filter, from its first and
 * last day in the month (YYYY-MM-DD, calendar days as the api gives them: no
 * time zone is involved): «5–11 ottobre», «1–4 ottobre» for a week the month
 * cuts, «31 ottobre» for one day. An unknown month shows its number.
 */
export function weekLabel(from: string, to: string): string {
  const day = (iso: string) => String(Number(iso.slice(8, 10)));
  const month = MONTHS_IT[Number(from.slice(5, 7)) - 1]?.toLowerCase() ?? from.slice(5, 7);
  return from === to ? `${day(from)} ${month}` : `${day(from)}–${day(to)} ${month}`;
}

const yearOnly = new Intl.DateTimeFormat("en-US", { timeZone: TIME_ZONE, year: "numeric" });

/** The current year in Europe/Rome (the headings of News salvate name the other years). */
export function romeYear(now: Date = new Date()): number {
  return Number(yearOnly.format(now));
}
